import { useState } from "react";
import { Button } from "flowbite-react";
import { HiBan, HiEye, HiPencil, HiRefresh, HiTrash } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Subcategory, DataGridPagination } from "@/sharedTypes";
import DataTable from "@/components/Table";
import SubcategoryFormDialog from "@/pages/subcategories/SubcategoryFormDialog";
import ConfirmationDialog from "@/components/ConfirmationDialog";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";
import {
  useDeleteSubcategoryMutation,
  useHardDeleteSubcategoryMutation,
  useRestoreSubcategoryMutation,
  useUpdateSubcategoryMutation,
} from "@/api/ticketApi/subcategory";
import { toast } from "@/lib/toast";
import { getErrorMessage } from "@/utils/requestUtils";

interface SubcategoryListTableProps {
  data: Subcategory[];
  pagination: DataGridPagination;
  onPageChange: (page: number) => void;
  showInactive: boolean;
  onRefresh?: () => void;
}

export default function SubcategoryListTable({
  data,
  pagination,
  onPageChange,
  showInactive,
  onRefresh,
}: SubcategoryListTableProps) {
  const { t } = useTranslation(["subcategory"]);
  const navigate = useNavigate();
  const user = useCurrentUser();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState<Subcategory | undefined>();
  const [confirmDialog, setConfirmDialog] = useState<{
    title: string;
    body: string;
    action: "disable" | "restore" | "hardDelete";
    onConfirm: () => void;
  } | null>(null);

  const [updateSubcategory] = useUpdateSubcategoryMutation();
  const [deleteSubcategory, { isLoading: isDeleting }] = useDeleteSubcategoryMutation();
  const [restoreSubcategory, { isLoading: isRestoring }] = useRestoreSubcategoryMutation();
  const [hardDeleteSubcategory, { isLoading: isHardDeleting }] =
    useHardDeleteSubcategoryMutation();

  const handleEdit = (subcategory: Subcategory) => {
    setSelectedSubcategory(subcategory);
    setIsEditModalOpen(true);
  };

  const handleUpdate = (updated: Subcategory) => {
    updateSubcategory({
      ...updated,
      subcategory_id: selectedSubcategory?.subcategory_id,
    } as Subcategory)
      .unwrap()
      .then(() => {
        toast.success(t("subcategory:updated_successfully"));
        setIsEditModalOpen(false);
        onRefresh?.();
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handleDisable = (id: number) => {
    setConfirmDialog({
      title: t("subcategory:disable_title"),
      body: t("subcategory:disable_confirm"),
      action: "disable",
      onConfirm: () => {
        deleteSubcategory(id)
          .unwrap()
          .then(() => {
            toast.success(t("subcategory:disabled_successfully"));
            setConfirmDialog(null);
            onRefresh?.();
          })
          .catch((err) => toast.error(getErrorMessage(err)));
      },
    });
  };

  const handleRestore = (id: number) => {
    setConfirmDialog({
      title: t("subcategory:restore_title"),
      body: t("subcategory:restore_confirm"),
      action: "restore",
      onConfirm: () => {
        restoreSubcategory(id)
          .unwrap()
          .then(() => {
            toast.success(t("subcategory:restored_successfully"));
            setConfirmDialog(null);
            onRefresh?.();
          })
          .catch((err) => toast.error(getErrorMessage(err)));
      },
    });
  };

  const handleHardDelete = (id: number) => {
    setConfirmDialog({
      title: t("subcategory:delete_title"),
      body: t("subcategory:delete_confirm"),
      action: "hardDelete",
      onConfirm: () => {
        hardDeleteSubcategory(id)
          .unwrap()
          .then(() => {
            toast.success(t("subcategory:deleted_successfully"));
            setConfirmDialog(null);
            onRefresh?.();
          })
          .catch((err) => toast.error(getErrorMessage(err)));
      },
    });
  };

  const columns = [
    { key: "subcategory_name", label: t("subcategory:subcategory_name") },
    { key: "subcategory_description", label: t("subcategory:description") },
    {
      key: "category",
      label: t("subcategory:category"),
      render: (row: Subcategory) => (
        <span className={row.category?.is_active === false ? "italic text-gray-400" : ""}>
          {row.category?.category_name || "—"}
        </span>
      ),
    },
    ...(isCompanyAdminUser(user)
      ? [
          {
            key: "actions",
            label: t("subcategory:actions"),
            render: (row: Subcategory) => (
              <div className="flex gap-2">
                <Button
                  size="xs"
                  color="light"
                  onClick={() =>
                    navigate(`/subcategories/${row.subcategory_id}`)
                  }
                >
                  <HiEye className="h-4 w-4 text-blue-600" />
                </Button>
                {!showInactive ? (
                  <>
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => handleEdit(row)}
                    >
                      <HiPencil className="h-4 w-4 text-purple-600" />
                    </Button>
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => handleDisable(Number(row.subcategory_id))}
                    >
                      <HiBan className="h-4 w-4 text-orange-600" />
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="xs"
                      color="light"
                      onClick={() => handleRestore(Number(row.subcategory_id))}
                    >
                      <HiRefresh className="h-4 w-4 text-green-600" />
                    </Button>
                    <Button
                      size="xs"
                      color="light"
                      onClick={() =>
                        handleHardDelete(Number(row.subcategory_id))
                      }
                    >
                      <HiTrash className="h-4 w-4 text-red-600" />
                    </Button>
                  </>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <DataTable
        columns={columns}
        data={data}
        pagination={pagination}
        onPageChange={onPageChange}
      />

      {isEditModalOpen && (
        <SubcategoryFormDialog
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSubmit={handleUpdate}
          mode="edit"
          initialData={selectedSubcategory}
        />
      )}

      {confirmDialog && (
        <ConfirmationDialog
          title={confirmDialog.title}
          body={confirmDialog.body}
          onCancel={() => setConfirmDialog(null)}
          onConfirm={confirmDialog.onConfirm}
          isLoading={
            confirmDialog.action === "disable"
              ? isDeleting
              : confirmDialog.action === "restore"
                ? isRestoring
                : isHardDeleting
          }
        />
      )}
    </>
  );
}
